import {
  Box,
  Button as ChakraButton,
  Flex,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { Modal, Select, showToast } from '@diamond/shared/ui';
import { useEffect, useMemo, useState } from 'react';

import { useUpdateLHCMutation } from '../hooks';
import type { EditLHCModalProps } from '../types';
import { SyncInfo } from './sync-info';

function toDateInputValue(value: string): string {
  if (!value) return '';

  if (/^\d+$/.test(value)) {
    const unixSeconds = Number(value);
    if (!Number.isNaN(unixSeconds)) {
      return new Date(unixSeconds * 1000).toISOString().split('T')[0];
    }
  }

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().split('T')[0];
  }

  return '';
}

export function EditLHCModal({
  isOpen,
  onClose,
  initialData,
  plantOptions,
  lastSyncedAt,
  onSync,
}: EditLHCModalProps) {
  const toast = useToast();

  const [lhcNumber, setLhcNumber] = useState('');
  const [lhcDate, setLhcDate] = useState('');
  const [selectedPlant, setSelectedPlant] = useState<SelectOption | null>(null);

  const initialState = useMemo(() => {
    if (!initialData) {
      return {
        lhcNumber: '',
        lhcDate: '',
        collectionPlantId: '',
      };
    }

    return {
      lhcNumber: initialData.lhcNo,
      lhcDate: toDateInputValue(initialData.lhcDateRaw),
      collectionPlantId: initialData.collectionPlantId,
    };
  }, [initialData]);

  useEffect(() => {
    if (!isOpen) return;

    setLhcNumber(initialState.lhcNumber);
    setLhcDate(initialState.lhcDate);

    const matchedPlant =
      plantOptions.find(
        (option) => option.value === initialState.collectionPlantId
      ) || null;
    setSelectedPlant(matchedPlant);
  }, [initialState, isOpen, plantOptions]);

  const updateLHCMutation = useUpdateLHCMutation();

  const isFormValid =
    lhcNumber.trim() !== '' && lhcDate !== '' && selectedPlant !== null;

  const isDirty =
    lhcNumber.trim() !== initialState.lhcNumber.trim() ||
    lhcDate !== initialState.lhcDate ||
    selectedPlant?.value !== initialState.collectionPlantId;

  const handleSubmit = () => {
    if (!initialData || !selectedPlant) return;

    updateLHCMutation.mutate(
      {
        id: initialData.id,
        payload: {
          lhc_no: lhcNumber.trim(),
          lhc_date: lhcDate,
          collection_plant_id: selectedPlant.value,
        },
      },
      {
        onSuccess: () => {
          showToast(toast, 'success', 'LHC berhasil diperbarui');
          onClose();
        },
        onError: (error: Error) => {
          showToast(toast, 'error', error.message || 'Gagal memperbarui LHC');
        },
      }
    );
  };

  return (
    <Modal
      title="Ubah Laporan Harian Collection"
      name="edit-lhc-modal"
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      isCentered
      hideAction
      scrollBehavior="outside"
      modalBodyProps={{ pb: 6 }}
      modalHeaderProps={{ fontSize: { base: '16px', md: '18px' }, pb: 2 }}
    >
      <Flex
        align="center"
        justify="space-between"
        mb={6}
        pb={4}
        borderBottom="1px solid"
        borderColor="gray.200"
        width="100%"
      >
        <SyncInfo lastSyncedAt={lastSyncedAt} onSync={onSync} />
      </Flex>

      <Flex gap={4} mb={6}>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Nomor LHC
          </FormLabel>
          <Input
            aria-label="Nomor LHC"
            placeholder="Tulis nomor LHC SOL"
            value={lhcNumber}
            onChange={(e) => setLhcNumber(e.target.value)}
            size="md"
          />
        </FormControl>

        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Tanggal LHC
          </FormLabel>
          <Input
            type="date"
            placeholder="Pilih tanggal"
            value={lhcDate}
            onChange={(e) => setLhcDate(e.target.value)}
            size="md"
          />
        </FormControl>

        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Plant / Cabang
          </FormLabel>
          <Box>
            <Select
              id="edit-lhc-plant"
              placeholder="Pilih lokasi"
              options={plantOptions}
              value={selectedPlant}
              onChange={(newValue) =>
                setSelectedPlant(newValue as SelectOption | null)
              }
            />
          </Box>
        </FormControl>
      </Flex>

      <Flex justify="flex-end">
        <ChakraButton
          bgColor={isFormValid && isDirty ? '#0000A3' : '#E8E8ED'}
          color={isFormValid && isDirty ? 'white' : '#8E8E93'}
          _hover={{
            bg: isFormValid && isDirty ? '#00008A' : '#E8E8ED',
          }}
          isDisabled={!isFormValid || !isDirty || updateLHCMutation.isPending}
          isLoading={updateLHCMutation.isPending}
          onClick={handleSubmit}
          px={6}
        >
          Simpan
        </ChakraButton>
      </Flex>
    </Modal>
  );
}
